import React, { useState } from 'react';
import {
  ObjFaqItem,
  CallInsightsAnalysis,
  getScoreBadgeClasses,
  getAdherenceLabelClasses,
  formatAdherenceLabel,
  formatCallInsightsTimestamp,
} from './types';
import SentimentRow from './SentimentRow';
import TranscriptBlock from './TranscriptBlock';
import ManagerReviewPanel from './ManagerReviewPanel';
import { describeConcernPeople } from '../callInsights/multiPartyFormat';

interface ObjFaqAdherenceSectionProps {
  items: ObjFaqItem[];
  overall_summary: string;
  sessionId: string;
  managerReview?: CallInsightsAnalysis['manager_review'];
  isAdmin: boolean;
  onReviewSaved: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
}

interface ExpandState {
  answers: Set<number>;
  transcripts: Set<number>;
}

const ObjFaqAdherenceSection: React.FC<ObjFaqAdherenceSectionProps> = ({
  items,
  overall_summary,
  sessionId,
  managerReview,
  isAdmin,
  onReviewSaved,
  onNavigateToTimestamp,
}) => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<ExpandState>({
    answers: new Set(),
    transcripts: new Set(),
  });

  const toggleCard = (idx: number) => {
    setExpandedCard((prev) => (prev === idx ? null : idx));
  };

  const toggleAnswers = (idx: number) => {
    setExpanded((prev) => {
      const next = new Set(prev.answers);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return { ...prev, answers: next };
    });
  };

  const toggleTranscripts = (idx: number) => {
    setExpanded((prev) => {
      const next = new Set(prev.transcripts);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return { ...prev, transcripts: next };
    });
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-12 text-ink-muted">
        No objections or FAQs were detected in this session.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {overall_summary && (
        <div className="bg-info-tint border border-info rounded-lg p-4 text-sm text-ink">
          <span className="font-semibold">Overall Summary: </span>
          {overall_summary}
        </div>
      )}

      {items.map((item, idx) => {
        const isOpen = expandedCard === idx;

        return (
          <div
            key={idx}
            className="bg-white border border-hairline rounded-lg shadow-sm overflow-hidden"
          >
            {/* Clickable header — always visible */}
            <button
              type="button"
              onClick={() => toggleCard(idx)}
              className="w-full flex flex-wrap items-start justify-between gap-2 p-4 text-left hover:bg-surface-subtle transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink text-sm leading-snug">
                  {item.question}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="text-xs bg-surface-subtle text-ink-secondary px-2 py-0.5 rounded">
                    {item.category}
                  </span>
                  {item.timestamp && (
                    <span className="text-xs text-ink-muted">{formatCallInsightsTimestamp(item.timestamp)}</span>
                  )}
                  {describeConcernPeople(item) && (
                    <span className="text-xs text-ink-muted">{describeConcernPeople(item)}</span>
                  )}
                  {item.timestamp_iso && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onNavigateToTimestamp?.(item.timestamp_iso!); }}
                      className="text-xs text-signature hover:text-signature-strong font-medium underline"
                    >
                      View in Timeline &rarr;
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}
                >
                  {item.handling_score}/100
                </span>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.adherence_label)}`}
                >
                  {formatAdherenceLabel(item.adherence_label)}
                </span>
                <span className="text-ink-muted text-xs ml-1">{isOpen ? '▲' : '▼'}</span>
              </div>
            </button>

            {/* Collapsible body */}
            {isOpen && (
              <div className="p-4 border-t border-hairline space-y-3">
                {item.adherence_summary && (
                  <p className="text-sm text-ink-secondary">{item.adherence_summary}</p>
                )}

                {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {item.what_worked && (
                      <div className="flex items-start gap-2 bg-success-tint border border-success rounded p-2.5 text-sm">
                        <span className="text-success font-bold flex-shrink-0 mt-0.5">&#10003;</span>
                        <p className="text-ink">{item.what_worked}</p>
                      </div>
                    )}
                    {(item.what_missed || item.what_rep_should_have_said) && (
                      <div className="bg-error-tint border border-error rounded p-2.5 text-sm space-y-2">
                        {item.what_missed && (
                          <div className="flex items-start gap-2">
                            <span className="text-error font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                            <p className="text-ink">{item.what_missed}</p>
                          </div>
                        )}
                        {item.what_rep_should_have_said && (
                          <div className={item.what_missed ? 'pt-2 border-t border-error' : ''}>
                            <p className="text-xs font-semibold text-error uppercase mb-1">Should Have Said</p>
                            <p className="text-ink">{item.what_rep_should_have_said}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {item.customer_reaction && (
                  <p className="text-sm text-ink-muted italic">
                    Customer reaction: {item.customer_reaction}
                  </p>
                )}

                <SentimentRow
                  before={item.sentiment_before}
                  after={item.sentiment_after}
                  trend={item.sentiment_trend}
                />

                {(item.stored_answer || item.ai_answer) && (
                  <div>
                    <button
                      onClick={() => toggleAnswers(idx)}
                      className="text-sm text-signature hover:text-signature-strong font-medium focus:outline-none"
                      type="button"
                    >
                      {expanded.answers.has(idx) ? 'Hide Suggested Answers' : 'Show Suggested Answers'}
                      <span className="ml-1">{expanded.answers.has(idx) ? '▲' : '▼'}</span>
                    </button>
                    {expanded.answers.has(idx) && (
                      <div className="mt-2 space-y-2">
                        {item.stored_answer && (
                          <div className="bg-surface-subtle border border-hairline rounded p-3 text-sm">
                            <p className="font-semibold text-ink-secondary mb-1">Knowledge Base Answer</p>
                            <p className="text-ink">{item.stored_answer}</p>
                          </div>
                        )}
                        {item.ai_answer && (
                          <div className="bg-info-tint border border-info rounded p-3 text-sm">
                            <p className="font-semibold text-info mb-1">AI-Generated Answer</p>
                            <p className="text-ink">{item.ai_answer}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <TranscriptBlock
                  transcripts={item.relevant_transcripts ?? []}
                  isExpanded={expanded.transcripts.has(idx)}
                  onToggle={() => toggleTranscripts(idx)}
                />
              </div>
            )}
          </div>
        );
      })}

      <ManagerReviewPanel
        sessionId={sessionId}
        currentSection="objection_faq"
        managerReview={managerReview}
        isAdmin={isAdmin}
        onReviewSaved={onReviewSaved}
      />
    </div>
  );
};

export default ObjFaqAdherenceSection;
